import { useEffect, useRef } from 'react';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useGitSyncStore } from '../store/gitSyncStore';
import { dequeue, queuedIds } from '../services/git/offlineQueue';
import { isRemoteLinked } from '../services/git/gitSyncStatus';

/** While something waits and the machine says it is online, try again this often. */
export const RETRY_MS = 2 * 60 * 1000;

/**
 * Sends the syncs that waited for the network (git sync Stage 1, card 7 set
 * 7d): when the app starts with some queued, when the OS reports the network
 * is back, and every couple of minutes while any still wait. Each retry is
 * the normal sync (`handleGitSync`) with `fromQueue`, so it never opens a
 * dialog by itself. A queued workspace that no longer syncs to a remote is
 * dropped from the queue.
 */
export function useGitOfflineQueue(handleGitSync) {
  const syncRef = useRef(handleGitSync);
  syncRef.current = handleGitSync;
  const running = useRef(false);

  useEffect(() => {
    const flush = async () => {
      if (running.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      const ids = queuedIds();
      if (!ids.length) return;
      running.current = true;
      try {
        const workspaces = useWorkspaceStore.getState().workspaces || [];
        for (const id of ids) {
          const ws = workspaces.find((w) => String(w.id) === id);
          // A local history (Track on this device) has nothing to send either.
          if (!isRemoteLinked(ws)) {
            if (workspaces.length) dequeue(id); // gone, unlinked or local — nothing to send
            continue;
          }
          useGitSyncStore.getState().recordQueued(id); // the badge says it waits, also after a restart
          // eslint-disable-next-line no-await-in-loop
          await syncRef.current?.(ws, { fromQueue: true });
        }
      } finally {
        running.current = false;
      }
    };

    // Workspaces load after mount: try once they are there.
    const unsubscribe = useWorkspaceStore.subscribe((s, prev) => {
      if ((s.workspaces || []).length && !(prev.workspaces || []).length) flush();
    });
    flush();
    window.addEventListener('online', flush);
    const timer = setInterval(flush, RETRY_MS);
    return () => {
      unsubscribe();
      window.removeEventListener('online', flush);
      clearInterval(timer);
    };
  }, []);
}
